/**
 * Type definitions for Rufus Spaces frontend
 * These types mirror the backend API types and must be kept in sync manually
 */

// Space status enum
export type SpaceStatus = 'draft' | 'building' | 'running' | 'stopped' | 'error';

// Space configuration
export interface SpaceConfig {
  cpu: number;
  memory: number;
  pythonVersion: '3.9' | '3.10' | '3.11';
  streamlitPort: number;
  environmentVariables?: Record<string, string>;
}

// Space model
export interface Space {
  spaceId: string;
  name: string;
  slug: string;
  owner: string;
  appType: 'streamlit';
  status: SpaceStatus;
  visibility: 'private' | 'internal';
  deploymentId?: string;
  url?: string;
  lastDeployedAt?: string;
  lastDeployedBy?: string;
  createdAt: string;
  updatedAt: string;
  config: SpaceConfig;
}

// File model
export interface SpaceFile {
  spaceId: string;
  filePath: string;
  s3Key: string;
  contentHash: string;
  size: number;
  currentVersion: string;
  lastModifiedBy: string;
  updatedAt: string;
}

// File version model
export interface FileVersion {
  spaceId: string;
  filePath: string;
  versionId: string;
  s3Key: string;
  contentHash: string;
  createdBy: string;
  createdAt: string;
  commitMessage?: string;
  previousVersionId?: string;
  changeType: 'created' | 'modified' | 'deleted';
}

// Template types
export interface TemplateFile {
  filePath: string;
  content: string;
}

export interface Template {
  templateId: string;
  name: string;
  description: string;
  appType: 'streamlit';
  files: TemplateFile[];
}

export interface TemplateSummary {
  templateId: string;
  name: string;
  description: string;
  appType: 'streamlit';
  fileCount: number;
}

// Collaborator types
export type CollaboratorRole = 'viewer' | 'editor' | 'admin';

export interface Collaborator {
  spaceId: string;
  userId: string;
  role: CollaboratorRole;
  addedBy: string;
  addedAt: string;
  updatedAt: string;
}

export interface AddCollaboratorInput {
  userId: string;
  role: CollaboratorRole;
}

export interface UpdateCollaboratorInput {
  role: CollaboratorRole;
}

export interface CollaboratorsListResponse {
  collaborators: Collaborator[];
}

// API request/response types
export interface CreateSpaceInput {
  name: string;
  appType?: 'streamlit';
  visibility?: 'private' | 'internal';
  templateId?: string;
}

export interface UpdateSpaceInput {
  name?: string;
  visibility?: 'private' | 'internal';
  config?: Partial<SpaceConfig>;
}

export interface SaveFileInput {
  content: string;
  commitMessage?: string;
}

// Paginated response types
export interface PaginatedResponse<T> {
  items: T[];
  nextCursor?: string;
}

export interface SpacesListResponse {
  spaces: Space[];
  nextCursor?: string;
}

export interface FilesListResponse {
  files: SpaceFile[];
}

export interface FileVersionsResponse {
  versions: FileVersion[];
  nextCursor?: string;
}

// API Error response
export interface ApiError {
  error: string;
  details?: unknown;
}

// File content response
export interface FileContentResponse {
  content: string;
  file: SpaceFile;
}

// Default space configuration values
export const DEFAULT_SPACE_CONFIG: SpaceConfig = {
  cpu: 256,
  memory: 512,
  pythonVersion: '3.10',
  streamlitPort: 8501,
};

// Status display helpers
export const STATUS_LABELS: Record<SpaceStatus, string> = {
  draft: 'Draft',
  building: 'Building',
  running: 'Running',
  stopped: 'Stopped',
  error: 'Error',
};

export const STATUS_COLORS: Record<SpaceStatus, string> = {
  draft: 'gray',
  building: 'blue',
  running: 'green',
  stopped: 'orange',
  error: 'red',
};

// Collaborator role display helpers
export const ROLE_LABELS: Record<CollaboratorRole, string> = {
  viewer: 'Viewer',
  editor: 'Editor',
  admin: 'Admin',
};

export const ROLE_DESCRIPTIONS: Record<CollaboratorRole, string> = {
  viewer: 'Can view files and settings',
  editor: 'Can view and edit files',
  admin: 'Can edit files, deploy, and manage collaborators',
};
